import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useProfile } from '@/contexts/CurrentUserContext/hooks';
import { useRequestEmailVerification } from '@/hooks/useRequestEmailVerification';

const COOLOFF_DURATION_MINUTES = 5;

const calculateCooloffEndTime = (sentAt?: DateTime): number | null => {
  if (!sentAt) return null;

  const endTime = sentAt.plus({ minutes: COOLOFF_DURATION_MINUTES });
  if (endTime.toMillis() > Date.now()) {
    return endTime.toMillis();
  }
  return null;
};

export const useEmailVerificationGate = () => {
  const { requestEmailVerification, isPending } = useRequestEmailVerification();
  const profile = useProfile();
  const { currentUser } = profile;
  const isEmailVerified = currentUser?.emailVerified ?? true;

  const [sentAt, setSentAt] = useLocalStorage<DateTime>(
    '_ah_verification_email_sent_at',
    undefined,
    {
      raw: false,
      serializer: (value) => value.toISO(),
      deserializer: (value) => DateTime.fromISO(value),
    }
  );

  const [cooloffEndTime, setCooloffEndTime] = useState<number | null>(() =>
    calculateCooloffEndTime(sentAt)
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    const endTime = calculateCooloffEndTime(sentAt);
    return endTime ? Math.max(0, endTime - Date.now()) : 0;
  });

  useEffect(() => {
    const newCooloffEndTime = calculateCooloffEndTime(sentAt);
    setCooloffEndTime((prev) => {
      if (newCooloffEndTime !== prev) {
        return newCooloffEndTime;
      }
      return prev;
    });
  }, [sentAt]);

  useEffect(() => {
    if (!cooloffEndTime) return;

    const interval = setInterval(() => {
      const remaining = cooloffEndTime - Date.now();
      if (remaining <= 0) {
        setCooloffEndTime(null);
        setTimeRemaining(0);
        setSentAt(undefined);
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooloffEndTime, setSentAt]);

  const handleResendClick = () => {
    requestEmailVerification();
    const now = DateTime.now();
    setSentAt(now);
    setCooloffEndTime(
      now.plus({ minutes: COOLOFF_DURATION_MINUTES }).toMillis()
    );
  };

  const formatTime = (ms: number) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return {
    isEmailVerified,
    isPending,
    cooloffEndTime,
    timeRemaining,
    handleResendClick,
    formatTime,
  };
};
